import { CustomerSettingsMessage } from '../../../../types/CustomerSettingsMessage';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { Switch } from '../../../cdl/Switch/Switch';
import { Text } from '../../../cdl/Text/Text';
import { TextButton } from '../../../common/buttons/TextButton';
import { translate } from '../../../common/helpers/translate.helper';
import { Box } from '../../../common/ui/Box';
import { useCustomerSettingsOptimisticMutation } from '../../hooks/useCustomerSettingsOptimisticMutation';

import { MultipleApproverSettingsSummary } from './MultipleApproversSettingsForm/components/MultipleApproverSettingsSummary';
import { MultipleApproversSettingsForm } from './MultipleApproversSettingsForm/MultipleApproversSettingsForm';
import { useCustomerSettingsEdit } from './useCustomerSettingsEdit';

interface ApprovalSettingsProps {
    customerSettings: CustomerSettingsMessage;
    customerId: string;
}

export const ApprovalSettings = (props: ApprovalSettingsProps) => {
    const { push, pop } = useDrawer();

    const updateCustomerSettingsMutation = useCustomerSettingsEdit();
    const editCustomerSettingsMutation = useCustomerSettingsOptimisticMutation(props.customerId);

    const openApprovalSettingsModal = () => {
        const onSubmit = async (customerSettings: CustomerSettingsMessage) => {
            await updateCustomerSettingsMutation.mutateAsync(
                {
                    id: props.customerId,
                    settings: customerSettings,
                },
                { onSuccess: () => pop(drawerId) }
            );
        };

        const drawerId = push({
            header: { title: translate('approvalSettings.editApprovalSettings') },
            content: <MultipleApproversSettingsForm customerSettings={props.customerSettings} onSubmit={onSubmit} />,
        });
    };

    const handleApprovalSettingsToggle = (checked: boolean) => {
        if (!props.customerSettings.approvalSettings.defaultControllerEmailAddress) {
            openApprovalSettingsModal();
        } else {
            const newCustomerSettings = props.customerSettings;
            newCustomerSettings.approvalSettings.activated = checked;
            updateCustomerSettingsMutation.mutate({
                id: props.customerId,
                settings: newCustomerSettings,
            });
        }
    };

    const updateSettings = (newSettings: Partial<CustomerSettingsMessage>) => {
        // @ts-ignore
        editCustomerSettingsMutation.mutate({
            id: props.customerId,
            settings: {
                ...props.customerSettings,
                ...newSettings,
            },
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Text variant="subtitle1" fontWeight={600}>
                {translate('approvalSettings.order')}
            </Text>

            <Switch
                checked={props.customerSettings.approvalSettings.activated}
                onChange={handleApprovalSettingsToggle}
                label={translate('approvalSettings.activatedLabel')}
                description={
                    <div>
                        {props.customerSettings.approvalSettings.activated ? (
                            <MultipleApproverSettingsSummary
                                approvalSettings={props.customerSettings.approvalSettings}
                            />
                        ) : null}
                        {props.customerSettings.approvalSettings.defaultControllerEmailAddress ? (
                            <Box marginTop={props.customerSettings.approvalSettings.activated ? 8 : 0}>
                                <TextButton onClick={openApprovalSettingsModal}>{translate('actions.edit')}</TextButton>
                            </Box>
                        ) : null}
                    </div>
                }
            />
            <Switch
                checked={props.customerSettings.automaticallyMarkAsDelivered}
                onChange={(checked) =>
                    updateSettings({
                        automaticallyMarkAsDelivered: checked,
                    })
                }
                label={translate('customers.details.automaticallyMarkAsDelivered')}
                description={translate('customers.details.automaticallyMarkAsDeliveredDescription')}
            />
        </Box>
    );
};
